/**
 * S-05 月次集計（自分）。
 *
 * 会社支給計算（稼働ベース）と法定判定（週40時間・実働ベース）を分けて表示する。
 * 基準が異なるため、同じ画面に混ぜると誤解を招く（画面仕様書 §8）。
 */

import { useState } from 'react';
import { endpoints } from '../../api/endpoints.js';
import { MonthNav } from '../../components/MonthNav.js';
import {
  Card,
  DataList,
  ErrorMessage,
  Loading,
  Notice,
  ProvisionalBadge,
} from '../../components/ui.js';
import { useMe } from '../../auth/context.js';
import { currentMonth, formatDateLabel, formatDuration } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function MonthlySummaryPage(): React.JSX.Element {
  const me = useMe();
  const [month, setMonth] = useState(currentMonth());
  const state = useAsync(() => endpoints.summary(month), [month]);

  return (
    <>
      <MonthNav month={month} onChange={setMonth} />

      <Notice tone="warn" title="給与確定前の勤怠集計です">
        制度未確定の項目を含むため試算表示です。金額は表示しません。
      </Notice>

      {state.error !== null && <ErrorMessage error={state.error} />}
      {state.loading && !state.data && <Loading />}

      {state.data && (
        <>
          <Card
            title={
              <span className="flex items-center gap-2">
                集計内訳 {me.provisional && <ProvisionalBadge />}
              </span>
            }
          >
            <DataList
              columns={3}
              items={[
                { label: '出勤日数', value: `${state.data.summary.attendanceDays}日` },
                { label: '休日出勤', value: `${state.data.summary.holidayWorkDays}日` },
                { label: '実働時間', value: state.data.summary.display.workHours },
                { label: '移動時間', value: state.data.summary.display.travelHours },
                { label: '稼働時間', value: state.data.summary.display.totalHours },
                { label: '深夜時間', value: state.data.summary.display.nightHours },
                {
                  label: '時間単位年休',
                  value: `${state.data.summary.hourlyLeaveHours}時間`,
                },
                { label: '食事手当', value: `${state.data.summary.mealAllowanceCount}回` },
                {
                  label: '休日8時間超',
                  value: formatDuration(state.data.summary.holidayOver8Minutes),
                },
              ]}
            />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card
              title="会社支給計算（稼働基準）"
              description="移動時間を含む稼働時間をもとにした社内の残業計算です。"
            >
              <DataList
                columns={2}
                items={[
                  {
                    label: '日残業合計',
                    value: formatDuration(state.data.summary.dailyOvertimeMinutes),
                  },
                  {
                    label: '週超過残業',
                    value: formatDuration(state.data.summary.weeklyOvertimeMinutes),
                  },
                  {
                    label: '月残業合計',
                    value: state.data.summary.display.monthlyOvertimeHours,
                  },
                  {
                    label: '月60時間超',
                    value: state.data.summary.display.overtimeOver60Hours,
                  },
                  {
                    label: '法定休日出勤',
                    value: formatDuration(state.data.summary.legalHolidayMinutes),
                  },
                  {
                    label: '所定休日出勤',
                    value: formatDuration(state.data.summary.prescribedHolidayMinutes),
                  },
                ]}
              />
            </Card>

            <Card
              title="法定判定（週40時間）"
              description="労働基準法上の判定は実働時間で行うため、上の稼働基準とは値が異なります。"
            >
              {state.data.weeklyLegalCheck.length === 0 ? (
                <p className="text-sm text-ink-muted">対象の週がありません。</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>週</th>
                      <th className="text-right">実働時間</th>
                      <th className="text-right">40時間超</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.weeklyLegalCheck.map((week) => (
                      <tr key={week.weekStart}>
                        <td className="whitespace-nowrap">{formatDateLabel(week.weekStart)}〜</td>
                        <td className="text-right">{formatDuration(week.workMinutes)}</td>
                        <td
                          className={`text-right ${week.overMinutes > 0 ? 'font-bold text-warn-600' : ''}`}
                        >
                          {week.overMinutes > 0 ? formatDuration(week.overMinutes) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card title={`未解決の確認事項 ${state.data.unresolvedIssues.length}件`}>
            {state.data.unresolvedIssues.length === 0 ? (
              <p className="text-sm text-ink-muted">確認が必要な項目はありません。</p>
            ) : (
              <ul className="divide-y divide-line text-sm">
                {state.data.unresolvedIssues.map((issue) => (
                  <li key={issue.id} className="flex items-center gap-3 py-2">
                    <span
                      className={`pill ${issue.severity === 'error' ? 'pill--danger' : 'pill--warn'}`}
                    >
                      {issue.severity === 'error' ? 'エラー' : '確認'}
                    </span>
                    <span className="whitespace-nowrap">{formatDateLabel(issue.date)}</span>
                    <span className="text-ink-sub">{issue.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}
